
import React from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Download,
  FileUp,
  PlusCircle,
  RefreshCw,
  Search 
} from "lucide-react";
import { useRedirectsManager } from "@/hooks/useRedirectsManager";
import RedirectsTable from "@/components/admin/redirects/RedirectsTable";
import RedirectStats from "@/components/admin/redirects/RedirectStats";
import RedirectDialog from "@/components/admin/redirects/RedirectDialog";
import DeleteDialog from "@/components/admin/redirects/DeleteDialog";
import ImportDialog from "@/components/admin/redirects/ImportDialog";
import ArticleRedirectImporter from "@/components/admin/redirects/ArticleRedirectImporter";
import CloudflareSyncButton from "@/components/admin/redirects/CloudflareSyncButton";
import RedirectSystemInfo from "@/components/admin/redirects/RedirectSystemInfo";

const RedirectsManager: React.FC = () => {
  const {
    redirects,
    stats,
    isLoading,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    redirectToEdit,
    showRedirectForm,
    setShowRedirectForm,
    showDeleteDialog,
    setShowDeleteDialog,
    redirectToDelete,
    showImportDialog,
    setShowImportDialog,
    handleCreateRedirect,
    handleEditRedirect,
    handleDeleteClick,
    handleConfirmDelete,
    handleBulkImport,
    handleExport,
    handleRedirectSaved,
    refreshRedirects,
  } = useRedirectsManager();

  return (
    <AdminLayout 
      title="URL Redirects" 
      backLink="/admin/dashboard" 
      backLabel="Back to Dashboard"
    >
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold">URL Redirects</h1>
            <p className="text-gray-500">
              Manage redirects to preserve SEO when changing URLs
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <CloudflareSyncButton className="h-9" />
            <Button
              variant="outline"
              size="sm"
              onClick={refreshRedirects}
              className="h-9"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              className="h-9"
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowImportDialog(true)}
              className="h-9"
            >
              <FileUp className="h-4 w-4 mr-2" />
              Import CSV
            </Button>
            <Button
              onClick={handleCreateRedirect}
              className="bg-elfGreen hover:bg-elfGreen-dark h-9"
            >
              <PlusCircle className="h-4 w-4 mr-2" />
              New Redirect
            </Button>
          </div>
        </div>

        {/* Add the Article Redirect Importer */}
        <ArticleRedirectImporter />

        <RedirectSystemInfo />

        <RedirectStats stats={stats} isLoading={isLoading} />

        <div className="flex flex-col md:flex-row gap-4 mb-4 items-end">
          <div className="w-full md:w-2/3">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
              <Input
                placeholder="Search redirects by path or notes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
          <div className="w-full md:w-1/3">
            <Select
              value={statusFilter}
              onValueChange={(value) => setStatusFilter(value as "all" | "active" | "inactive")}
            >
              <SelectTrigger>
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Redirects</SelectItem>
                <SelectItem value="active">Active Only</SelectItem>
                <SelectItem value="inactive">Inactive Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <RedirectsTable
          redirects={redirects}
          isLoading={isLoading}
          onEdit={handleEditRedirect}
          onDelete={handleDeleteClick}
        />
      </div>

      {/* Dialogs */}
      <RedirectDialog
        open={showRedirectForm}
        onOpenChange={setShowRedirectForm}
        redirect={redirectToEdit}
        onSuccess={handleRedirectSaved}
      />

      <DeleteDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        redirect={redirectToDelete}
        onConfirm={handleConfirmDelete}
      />

      <ImportDialog
        open={showImportDialog}
        onOpenChange={setShowImportDialog}
        onImport={handleBulkImport}
        isImporting={false}
      />
    </AdminLayout>
  );
};

export default RedirectsManager;
